import { createContext, useContext, useEffect, useState, useCallback } from "react";

/**
 * Theme provider with three modes: "light", "dark", "system".
 *
 * - Persists the user's choice in localStorage under "ap.theme".
 * - On first load (no stored choice) falls back to "system" which mirrors
 *   the OS preference via `prefers-color-scheme`.
 * - Toggles the `.dark` class on <html> so Tailwind's `dark:` variant kicks in.
 */

const STORAGE_KEY = "ap.theme";
const ThemeContext = createContext(null);

function getSystemPrefersDark() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function applyThemeClass(effective) {
  const root = document.documentElement;
  if (effective === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
  // Set the data-theme attribute too. This is what the "Black Glass"
  // premium tokens (index.css :root[data-theme="dark"|"light"]) key
  // off, and it lets non-Tailwind CSS query the theme without needing
  // to inspect the class list.
  root.setAttribute("data-theme", effective);
  // Make native form controls (selects, scrollbars) match.
  root.style.colorScheme = effective;
}

export function ThemeProvider({ children, defaultTheme = "system" }) {
  const [theme, setThemeState] = useState(() => {
    if (typeof window === "undefined") return defaultTheme;
    return localStorage.getItem(STORAGE_KEY) || defaultTheme;
  });

  // Compute and apply effective theme whenever `theme` or system preference changes.
  useEffect(() => {
    const effective = theme === "system" ? (getSystemPrefersDark() ? "dark" : "light") : theme;
    applyThemeClass(effective);
  }, [theme]);

  // Listen for OS-level changes while in "system" mode.
  useEffect(() => {
    if (theme !== "system" || typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => applyThemeClass(mq.matches ? "dark" : "light");
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, [theme]);

  const setTheme = useCallback((next) => {
    setThemeState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch (_e) { /* private mode */ }
  }, []);

  const toggle = useCallback(() => {
    setTheme(
      theme === "dark"
        ? "light"
        : theme === "light"
          ? "dark"
          : getSystemPrefersDark()
            ? "light"
            : "dark"
    );
  }, [theme, setTheme]);

  const effective = theme === "system" ? (getSystemPrefersDark() ? "dark" : "light") : theme;

  return (
    <ThemeContext.Provider value={{ theme, effective, setTheme, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
  return ctx;
};
